<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <validation-observer ref="iCadRules">
            <b-form @submit.prevent="handleSubmit">
              <b-row class="match-height">
                <!-- Start Date-->
                <b-col md="5">
                  <DatePickerField
                    id="Start-date"
                    v-model="iCadReport.startDate"
                    :min-date="new Date()"
                    :label="$t('Start Date')"
                    :config="datePickerConfig"
                    :placeholder="$t('Select Date')"
                  />
                </b-col>
                <!--  Start Date -->

                <!-- End Date-->
                <b-col md="5">
                  <DatePickerField
                    id="end-date"
                    v-model="iCadReport.endDate"
                    :min-date="new Date()"
                    :label="$t('End Date')"
                    :config="datePickerConfig"
                    :placeholder="$t('Select Date')"
                  />
                </b-col>
                <!--  End Date -->
                <b-col
                  md="2"
                  class="mt-2"
                >
                  <Button
                    v-if="getUserPermission('ICAD_REPORT')"
                    :label="$t('Generate')"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    variant="primary"
                    size="md"
                    @click="fetchICADReport"
                  />
                </b-col>
              </b-row>
            </b-form>
          </validation-observer>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-dropdown
              v-if="getUserPermission('EXPORT_ICAD_REPORT')"
              id="dropdown-1"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              :text="$t('Export')"
              variant="primary"
            >
              <b-dropdown-item @click="csvExport(csvData)">
                {{ $t('CSV') }}
              </b-dropdown-item>
              <b-dropdown-item @click="excelExport(csvData)">
                {{ $t('Excel') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </b-col>

      </b-row>

    </div>

    <b-table
      ref="savingsAccrualHistoryTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="SN"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(dateOfBirth)="data">
        <span class="text-nowrap">
          {{ data.value }}
        </span>
      </template>
      <template #cell(dateOpened)="data">
        <span class="text-nowrap">
          {{ data.value }}
        </span>
      </template>
      <template #cell(accountStatus)="data">
        <span class="text-nowrap">
          {{ data.value }}
        </span>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BDropdown, BDropdownItem, BForm,
} from 'bootstrap-vue'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { ValidationObserver } from 'vee-validate'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { toBalance, formatAmount } from '@/helpers/formatAmount'
import DatePickerField from '../../../forms/DatePickerField.vue'
import TablePagination from '@/views/components/table/TablePagination.vue'
import getStatusColor from '../../../../helpers/getStatusColor'
import Button from '../../../components/button/Button.vue'
import toNumber from '../../../../helpers/toNumber'
import { customDate, toCustomDate, toDateWithYear } from '../../../../helpers/formatDate'
import { reportDataDetails } from '../../../../helpers/data'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BSpinner,
    BTable,
    TablePagination,
    BDropdown,
    BDropdownItem,
    ValidationObserver,
    DatePickerField,
    Button,
    BForm,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      formatAmount,
      toBalance,
      getStatusColor,
      isDisabled: false,
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: this.$t('ICAD Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'userID', sortable: true, label: 'User ID' },
        { key: 'dateOfBirth', sortable: true, label: 'Date Of Birth' },
        { key: 'email', sortable: true, label: 'Email' },
        { key: 'firstName', sortable: true, label: 'First Name' },
        { key: 'middleName', sortable: true, label: 'Other Name' },
        { key: 'lastName', sortable: true, label: 'Last Name' },
        { key: 'oldAccountNumber', sortable: true, label: 'Old Account Number' },
        { key: 'phoneNumber', sortable: true, label: 'Phone Number' },
        { key: 'accountDesignation', sortable: true, label: 'Account Designation' },
        { key: 'accountStatus', sortable: true, label: 'Account Status' },
        { key: 'accountType', sortable: true, label: 'Account Type' },
        { key: 'bvn', sortable: true, label: 'BVN' },
        { key: 'pep', sortable: true, label: 'PEP' },
        { key: 'sectorCode', sortable: true, label: 'Sector Code' },
        { key: 'tin', sortable: true, label: 'TIN' },
        { key: 'dateOpened', sortable: true, label: 'Date Opened' },
        { key: 'accountTier', sortable: true, label: 'Account Tier' },
      ],
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      iCadReport: {
        startDate: '',
        endDate: '',
      },
      reportData: [],
      branchRefCode: '',
      selected: '',
      reportInfo: reportDataDetails,
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchICADReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchICADReport()
    },
  },
  mounted() {
    this.fetchICADReport()
  },
  methods: {
    ...mapActions({
      getICADReport: 'reports/getICADReport',
      downloadICADReport: 'reports/downloadICADReport',
    }),

    async fetchICADReport() {
      try {
        this.isBusy = true
        const response = await this.getICADReport({
          search: this.iCadReport,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.reportData = data.data
          this.totalCount = data.recordCount
          this.isDisabled = false
          this.isBusy = false
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadICADReport({
          search: this.iCadReport,
          fileExportFormat: 1,
          permission: reportPermissions.EXPORT_ICAD_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: 'Your download is in progress and might take a little longer. Go to your ',
            link: {
              text: 'Download Manager',
              route: { name: 'download-manager' },
            },
            afterLinkText: ' to access the completed file.',
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadICADReport({
          search: this.iCadReport,
          fileExportFormat: 0,
          permission: reportPermissions.EXPORT_ICAD_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: 'Your download is in progress and might take a little longer. Go to your',
            link: {
              text: 'Download Manager',
              route: { name: 'download-manager' },
            },
            afterLinkText: ' to access the completed file.',
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    formatNumber: toNumber,
    formatCustomDateYear: customDate,
    formatCustomDate: toCustomDate,
    toDate: toDateWithYear,
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
